import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { TelegramUser, User } from '@wbstats/shared';
import { createApiUrl } from '../../config/api';

export interface UserWithId extends User {
  _id: string;
}

interface UserState {
  currentUser: UserWithId | null;
  isLoading: boolean;
  error: string | null;
  initialized: boolean;
}

interface ApiResponse<T> {
  message: string;
  data: T;
}

interface AuthResponse {
  user: UserWithId;
  token?: string;
}

const API_URL = createApiUrl('auth');

export const checkToken = createAsyncThunk<
  UserWithId,
  void,
  {
    rejectValue: string | { message: string };
  }
>('user/checkToken', async (_, { rejectWithValue }) => {
  const token = localStorage.getItem('token');
  if (!token) {
    return rejectWithValue('no_token');
  }

  try {
    const response = await fetch(`${API_URL}/verify`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const data = (await response.json()) as ApiResponse<AuthResponse>;

    if (!response.ok) {
      localStorage.removeItem('token');
      return rejectWithValue({ message: data.message });
    }

    return data.data.user;
  } catch (error) {
    return rejectWithValue({ message: 'Не удалось проверить токен' });
  }
});

export const authenticateWithTelegram = createAsyncThunk<
  UserWithId,
  TelegramUser,
  {
    rejectValue: { message: string };
  }
>('user/authenticateWithTelegram', async (telegramUser: TelegramUser, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_URL}/telegram`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(telegramUser),
    });

    const data = (await response.json()) as ApiResponse<AuthResponse>;

    if (!response.ok) {
      return rejectWithValue({ message: data.message });
    }

    localStorage.setItem('token', data.data.token || '');
    return data.data.user;
  } catch (error) {
    console.error('Authentication error:', error);
    return rejectWithValue({
      message: error instanceof Error ? error.message : 'Failed to authenticate with Telegram',
    });
  }
});

export const logout = createAsyncThunk('user/logout', async () => {
  await Promise.resolve();
  localStorage.removeItem('token');
});

export const saveWbToken = createAsyncThunk<
  UserWithId,
  string,
  {
    rejectValue: { message: string };
  }
>('user/saveWbToken', async (token: string, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_URL}/save-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({ wb_api_token: token }),
    });

    const data = (await response.json()) as ApiResponse<AuthResponse>;

    if (!response.ok) {
      return rejectWithValue({ message: data.message });
    }

    return data.data.user;
  } catch (error) {
    return rejectWithValue({
      message: 'Не удалось проверить токен. Пожалуйста, проверьте подключение к интернету.',
    });
  }
});

export const deleteWbToken = createAsyncThunk<
  UserWithId,
  void,
  {
    rejectValue: { message: string };
  }
>('user/deleteWbToken', async (_, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_URL}/delete-token`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });

    const data = (await response.json()) as ApiResponse<AuthResponse>;

    if (!response.ok) {
      return rejectWithValue({ message: data.message });
    }

    return data.data.user;
  } catch (error) {
    return rejectWithValue({
      message: 'Не удалось удалить токен. Пожалуйста, попробуйте позже.',
    });
  }
});

const initialState: UserState = {
  currentUser: null, // Remove mock user, start with null
  isLoading: false,
  error: null,
  initialized: false,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(checkToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentUser = action.payload;
        state.error = null;
        state.initialized = true;
      })
      .addCase(checkToken.rejected, (state, action) => {
        state.isLoading = false;
        state.currentUser = null;
        state.error =
          action.payload === 'no_token'
            ? null
            : (typeof action.payload === 'object'
                ? action.payload.message
                : action.error.message) || 'Token verification failed';
        state.initialized = true;
      })
      .addCase(authenticateWithTelegram.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(authenticateWithTelegram.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentUser = action.payload;
        state.error = null;
      })
      .addCase(authenticateWithTelegram.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Произошла неизвестная ошибка';
      })
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.currentUser = null;
        state.error = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Logout failed';
      })
      .addCase(saveWbToken.fulfilled, (state, action) => {
        state.currentUser = action.payload;
        state.error = null;
      })
      .addCase(saveWbToken.rejected, (state, action) => {
        state.error = action.payload?.message || 'Произошла неизвестная ошибка';
      })
      .addCase(deleteWbToken.fulfilled, (state, action) => {
        state.currentUser = action.payload;
        state.error = null;
      })
      .addCase(deleteWbToken.rejected, (state, action) => {
        state.error = action.payload?.message || 'Произошла неизвестная ошибка';
      });
  },
});

export default userSlice.reducer;
